import { useState, useEffect, useRef } from 'react';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';

const PROCESSED_MATCHES_KEY = 'playmitos_processed_rejected_matches';

// Exportar la conexión global de WebSocket
export let globalWebSocket = null;
let globalConnectionAttempts = 0;
const MAX_CONNECTION_ATTEMPTS = 5;
let isConnectionBlocked = false;
const BLOCK_DURATION = 60000; // 1 minuto

const checkSessionBeforeRequest = async () => {
  try {
    const response = await api.get('/api/auth/me');
    return response.data.authenticated;
  } catch (error) {
    console.error('Error checking session:', error);
    return false;
  }
};

const useNotifications = () => {
  // Estados
  const [matches, setMatches] = useState([]);
  const [rejectedMatches, setRejectedMatches] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);

  // Refs
  const wsRef = useRef(null);
  const mountedRef = useRef(true);

  // Context
  const { user } = useContext(AuthContext);

  // Función para calcular el contador de notificaciones
  const calculateNotificationCount = () => {
    if (!user?.id) return 0;

    const userId = String(user.id);
    
    // Contar partidas pendientes que necesitan aprobación
    const pendingCount = matches.filter(match => {
      const team1Player = match.team1?.players?.find(p => String(p.user_id) === userId);
      const team2Player = match.team2?.players?.find(p => String(p.user_id) === userId);
      
      return (team1Player?.approval_status === 'pending' || team2Player?.approval_status === 'pending');
    }).length;

    // Contar partidas rechazadas
    const rejectedCount = rejectedMatches.filter(match => {
      const isUserInvolved = match.team1?.players?.some(p => p.user_id === user.id) || 
                           match.team2?.players?.some(p => p.user_id === user.id) ||
                           match.created_by === user.id;
      return isUserInvolved;
    }).length;

    return pendingCount + rejectedCount;
  };

  // Función para actualizar el contador
  const updateNotificationCount = () => {
    setNotificationCount(calculateNotificationCount());
  };

  // Función para obtener partidas pendientes
  const fetchPendingMatches = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [matchesRes, usersRes] = await Promise.all([
        api.get('/api/matches/pending'),
        api.get('/api/users')
      ]);
      
      const newUsersMap = {};
      usersRes.data.forEach(u => {
        newUsersMap[u.id] = u.username;
      });
      setUsersMap(newUsersMap);
      
      const filteredMatches = matchesRes.data.filter(match => {
        const isUserInTeam1 = match.team1?.players?.some(p => p.user_id === user.id);
        const isUserInTeam2 = match.team2?.players?.some(p => p.user_id === user.id);
        if (!isUserInTeam1 && !isUserInTeam2) {
          return false;
        }
        const userTeam = isUserInTeam1 ? match.team1 : match.team2;
        const userPlayer = userTeam.players.find(p => p.user_id === user.id);
        return userPlayer?.approval_status === 'pending';
      });
      setMatches(filteredMatches);
      setError(null);
      setIsConnected(true);
    } catch (err) {
      console.error('Error fetching pending matches:', err);
      setError(err.message);
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener partidas rechazadas
  const fetchRejectedMatches = async () => {
    try {
      const response = await api.get('/api/matches/rejected');
      const matches = response.data?.matches || response.data || [];
      const filteredMatches = matches.filter(match => {
        const isUserInvolved = match.team1?.players?.some(p => p.user_id === user?.id) || 
                             match.team2?.players?.some(p => p.user_id === user?.id) ||
                             match.created_by === user?.id;
        if (!isUserInvolved) return false;
        if (match.approval_status !== 'rejected') return false;
        const didUserReject = match.team1?.players?.some(p => p.user_id === user?.id && p.approval_status === 'rejected') ||
                            match.team2?.players?.some(p => p.user_id === user?.id && p.approval_status === 'rejected');
        if (didUserReject) return false;
        return true;
      });
      setRejectedMatches(filteredMatches);
    } catch (error) {
      console.error('Error fetching rejected matches:', error);
    }
  };

  // Función para aprobar una partida
  const handleApprove = async (matchId) => {
    try {
      await api.post(`/api/matches/${matchId}/approve`);
      await Promise.all([
        fetchPendingMatches(),
        fetchRejectedMatches()
      ]);
      updateNotificationCount();
    } catch (err) {
      console.error('Error approving match:', err);
      throw err;
    }
  };

  // Función para rechazar una partida
  const handleReject = async (matchId) => {
    try {
      await api.post(`/api/matches/${matchId}/reject`);
      await Promise.all([
        fetchPendingMatches(),
        fetchRejectedMatches()
      ]);
      updateNotificationCount();
    } catch (err) {
      console.error('Error rejecting match:', err);
      throw err;
    }
  };

  // Función para manejar el OK de una partida rechazada
  const handleOk = async (matchId) => {
    try {
      await api.post(`/api/matches/${matchId}/handle-rejection`, { action: 'ok' });
      await fetchRejectedMatches();
      updateNotificationCount();
      return true;
    } catch (err) {
      console.error('Error handling rejection OK:', err);
      throw err;
    }
  };

  // Función para crear una nueva partida
  const handleCreateNew = async (match) => {
    try {
      const newMatchData = {
        match_date: match.match_date,
        match_mode: match.match_mode,
        tier: match.tier,
        team1_players: match.team1.players.map(p => p.user_id),
        team2_players: match.team2.players.map(p => p.user_id),
        team1_decks: match.team1.decks?.map(d => d.deck_id) || [],
        team2_decks: match.team2.decks?.map(d => d.deck_id) || [],
        team1_score: match.team1.score || 0,
        team2_score: match.team2.score || 0,
        winning_team: match.winning_team || null,
        highlights: match.highlights || []
      };
      await api.post('/api/matches', newMatchData);
      await Promise.all([
        fetchPendingMatches(),
        fetchRejectedMatches()
      ]);
      updateNotificationCount();
      return true;
    } catch (err) {
      console.error('Error creating new match:', err);
      throw err;
    }
  };

  // Función para refrescar las notificaciones
  const refreshNotifications = async () => {
    try {
      await Promise.all([
        fetchPendingMatches(),
        fetchRejectedMatches()
      ]);
      updateNotificationCount();
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    }
  };

  // Función para limpiar las notificaciones
  const cleanupNotifications = async () => {
    try {
      setMatches([]);
      setRejectedMatches([]);
      setNotificationCount(0);
      await Promise.all([
        fetchPendingMatches(),
        fetchRejectedMatches()
      ]);
    } catch (error) {
      console.error('Error cleaning up notifications:', error);
    }
  };

  // Effect para actualizar el contador
  useEffect(() => {
    if (user?.id) {
      updateNotificationCount();
    }
  }, [matches, rejectedMatches, user?.id]);

  // Effect para inicializar las partidas
  useEffect(() => {
    if (user?.id) {
      Promise.all([
        fetchPendingMatches(),
        fetchRejectedMatches()
      ]).catch(error => {
        console.error('Error fetching matches:', error);
      });
    }
  }, [user?.id]);

  // Effect para manejar la conexión WebSocket
  useEffect(() => {
    mountedRef.current = true;
    
    if (user?.id) {
      const wsUrl = `${import.meta.env.VITE_WS_URL}/ws?userId=${user.id}`;
      if (wsUrl) {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setIsConnected(true);
          setError(null);
        };

        ws.onerror = (error) => {
          setIsConnected(false);
          setError('Error de conexión WebSocket');
        };

        ws.onclose = () => {
          setIsConnected(false);
          wsRef.current = null;
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'new_notification') {
              setNotificationCount(message.count);
              Promise.all([
                fetchPendingMatches(),
                fetchRejectedMatches()
              ]).catch(err => {
                console.error('Error updating matches after notification:', err);
              });
            }
          } catch (error) {
            console.error('Error processing WebSocket message:', error);
          }
        };
      }
    }

    return () => {
      mountedRef.current = false;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user?.id]);

  // Early return if no user
  if (!user) {
    return {
      notificationCount: 0,
      matches: [],
      rejectedMatches: [],
      usersMap: {},
      loading: false,
      isConnected: false,
      error: null,
      handleApprove: () => Promise.reject(new Error('Usuario no autenticado')),
      handleReject: () => Promise.reject(new Error('Usuario no autenticado')),
      handleOk: () => Promise.reject(new Error('Usuario no autenticado')),
      handleCreateNew: () => Promise.reject(new Error('Usuario no autenticado')),
      fetchPendingMatches: () => Promise.reject(new Error('Usuario no autenticado')),
      fetchRejectedMatches: () => Promise.reject(new Error('Usuario no autenticado')),
      cleanupNotifications: () => Promise.reject(new Error('Usuario no autenticado')),
      refreshNotifications: () => Promise.reject(new Error('Usuario no autenticado'))
    };
  }

  return {
    notificationCount,
    matches,
    rejectedMatches,
    usersMap,
    loading,
    isConnected,
    error,
    handleApprove,
    handleReject,
    handleOk,
    handleCreateNew,
    fetchPendingMatches,
    fetchRejectedMatches,
    cleanupNotifications,
    refreshNotifications
  };
};

export default useNotifications; 